import { EChartsOption } from 'echarts';

// Re-export from the shared location for backward compatibility.
export { watchBodyDarkMode$ } from '@examdojo/styles';

const WHITE_VALUES = new Set(['#fff', '#ffffff']);

/**
 * Standard Tailwind hex values commonly hardcoded in ECharts configs,
 * mapped to their CSS custom-property equivalents.
 *
 * Only applied in dark mode — the original Tailwind values look correct
 * on a light background, but are invisible or clashing on a dark one.
 */
const TAILWIND_HEX_TO_CSS_VAR: Array<[string, string]> = [
  // Grays (light → dark)
  ['#f3f4f6', '--neutral-100'], // gray-100 → grid / split lines
  ['#e5e7eb', '--neutral-200'], // gray-200
  ['#d1d5db', '--neutral-300'], // gray-300 → axis lines
  ['#9ca3af', '--neutral-400'], // gray-400
  ['#6b7280', '--neutral-500'], // gray-500 → axis labels
  // Dark text → light text
  ['#374151', '--neutral-700'], // gray-700 → tooltip text
  // White → background
  ['#fff', '--neutral-0'],
  ['#ffffff', '--neutral-0'],
];

/** Deep-walk, strip white backgrounds, and optionally remap colours. */
function walkOptions(obj: unknown, colorMap: Map<string, string> | null): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => walkOptions(item, colorMap));
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Strip white backgroundColor in light mode (invisible on white).
    // In dark mode, let it fall through to the color remap so it becomes the dark bg.
    if (key === 'backgroundColor' && typeof value === 'string' && WHITE_VALUES.has(value.toLowerCase()) && !colorMap) {
      continue;
    }
    if (typeof value === 'string' && colorMap) {
      result[key] = colorMap.get(value.toLowerCase()) ?? value;
    } else if (typeof value === 'object') {
      result[key] = walkOptions(value, colorMap);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Apply global defaults and sanitise an ECharts option tree.
 *
 * Always:
 *  - Strips white `backgroundColor` values (invisible on white, broken on dark).
 *
 * Dark mode only:
 *  - Replaces hardcoded Tailwind gray hex values with the project's
 *    CSS-custom-property equivalents so axes / labels / grid lines are visible.
 */
export function adaptEchartsOptions(options: EChartsOption, doc: Document, isDark: boolean): EChartsOption {
  let colorMap: Map<string, string> | null = null;

  if (isDark) {
    const style = getComputedStyle(doc.body);
    colorMap = new Map<string, string>();
    for (const [hex, cssVar] of TAILWIND_HEX_TO_CSS_VAR) {
      const resolved = style.getPropertyValue(cssVar).trim();
      if (resolved) {
        colorMap.set(hex, resolved);
      }
    }

    // Map rgba white backgrounds to the dark-mode equivalent
    const neutral0 = style.getPropertyValue('--neutral-0').trim();
    if (neutral0) {
      const r = parseInt(neutral0.slice(1, 3), 16);
      const g = parseInt(neutral0.slice(3, 5), 16);
      const b = parseInt(neutral0.slice(5, 7), 16);
      colorMap.set('rgba(255,255,255,0.92)', `rgba(${r},${g},${b},0.92)`);
    }

    // Map near-invisible black hover band to a visible band for dark mode
    const neutral400 = style.getPropertyValue('--neutral-400').trim();
    if (neutral400) {
      const r4 = parseInt(neutral400.slice(1, 3), 16);
      const g4 = parseInt(neutral400.slice(3, 5), 16);
      const b4 = parseInt(neutral400.slice(5, 7), 16);
      const hoverBand = `rgba(${r4},${g4},${b4},0.2)`;
      colorMap.set('rgba(0, 0, 0, 0.04)', hoverBand);
      colorMap.set('rgba(0,0,0,0.04)', hoverBand);
    }

    if (colorMap.size === 0) {
      colorMap = null;
    }
  }

  const result = walkOptions(options, colorMap) as Record<string, unknown>;

  // Disable eCharts' default series clipping so bars/markers at grid edges
  // are not cut off.  Honour any explicit clip already set by the author.
  const series = result['series'];
  if (Array.isArray(series)) {
    result['series'] = series.map((s) => {
      if (s && typeof s === 'object' && !Array.isArray(s) && !('clip' in s)) {
        return { ...s, clip: false };
      }
      return s;
    });
  }

  // Auto-generate a probability tooltip formatter for Sankey series
  // that have `_probabilityTooltip: true` set in their JSON config.
  const seriesArr = result['series'] as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(seriesArr)) {
    for (const s of seriesArr) {
      if (s?.['type'] === 'sankey' && s['_probabilityTooltip']) {
        delete s['_probabilityTooltip'];

        const sourceTotals: Record<string, number> = {};
        const targetTotals: Record<string, number> = {};
        const links = s['links'] as Array<{ source: string; target: string; value: number }> | undefined;
        if (Array.isArray(links)) {
          for (const link of links) {
            sourceTotals[link.source] = (sourceTotals[link.source] || 0) + link.value;
            targetTotals[link.target] = (targetTotals[link.target] || 0) + link.value;
          }
        }

        const stripParens = (name: string) => {
          const lines = name.split('\n');
          const last = lines[lines.length - 1].replace(/\s*\(.*?\)\s*$/, '').trim();
          return last || lines[0].trim();
        };

        const existing = (result['tooltip'] as Record<string, unknown>) || {};
        result['tooltip'] = {
          ...existing,
          formatter(params: { dataType?: string; data?: Record<string, unknown>; name?: string; value?: number }) {
            if (params.dataType === 'edge') {
              const src = params.data?.['source'] as string;
              const tgt = params.data?.['target'] as string;
              const val = params.data?.['value'] as number;
              const sTotal = sourceTotals[src] || val;
              const tTotal = targetTotals[tgt] || val;
              const shortSrc = stripParens(src);
              const shortTgt = stripParens(tgt);
              const pFwd = ((val / sTotal) * 100).toFixed(1);
              const pRev = ((val / tTotal) * 100).toFixed(1);
              return [
                `<b>${shortSrc} → ${shortTgt}</b>: ${val}`,
                `P(${shortTgt} | ${shortSrc}) = ${val}/${sTotal} ≈ ${pFwd}%`,
                `P(${shortSrc} | ${shortTgt}) = ${val}/${tTotal} ≈ ${pRev}%`,
              ].join('<br/>');
            }
            return params.name ?? '';
          },
        };
        break;
      }
    }
  }

  return result as EChartsOption;
}
